/*
Licensed Materials - Property of IBM
694906H
(c) Copyright IBM Corp.  2020 All Rights Reserved

US Government Users Restricted Rights - Use, duplication or disclosure restricted
by GSA ADP Schedule Contract with IBM Corp.
*/

import { useIntl } from '@exo/frontend-common-i18n';
import { Button } from '@exo/frontend-components-base';
import { Field, isRequired, TextInput, onChangeResolver } from '@exo/frontend-components-forms';
import { yupResolver } from '@hookform/resolvers/yup';
import { AxiosError } from 'axios';
import React, { useEffect, useState } from 'react';
import { Mode, useForm } from 'react-hook-form';
import { ObjectSchema } from 'yup';
import { PolarisLoginBaseForm } from '../PolarisLoginBaseForm/PolarisLoginBaseForm';
import * as S from './PolarisLoginForm.styles';

export const PolarisLoginForm = React.forwardRef<HTMLFormElement>(
  (
    {
      mode = 'onBlur',
      children,
      idPrefix = 'loginForm',
      schema,
      data,
      intlPrefix,
      onChange,
      onSubmit,
      submitResponse,
      errors
    }: Props,
    ref
  ) => {
    const { register, handleSubmit, formState, control } = useForm({
      mode,
      ...(schema && { resolver: onChangeResolver(onChange, yupResolver(schema)) })
    });

    const intl = useIntl(intlPrefix);
    const [errMsg, setErrMsg] = useState<string>('');
    if (submitResponse?.responseURL) {
      window.location.href = submitResponse.responseURL;
    }
    useEffect(() => {
      if (errors) {
        let tmpMsg: string = intl.msg('defaultErr', 'Something went wrong') as string;
        if (errors?.response?.status == 401) {
          tmpMsg = errors?.response?.data?.message;
        }
        setErrMsg(tmpMsg);
      }
    }, [errors]);

    const handleFormSubmit = input => {
      if (onSubmit) {
        setErrMsg('');
        onSubmit(input);
      }
    };
    return (
      <S.FormContainer>
        <PolarisLoginBaseForm
          form={{ handleSubmit }}
          ref={ref}
          onSubmit={handleFormSubmit}
          error={errMsg}
        >
          <Field className="no-bottom">
            <TextInput
              id={`${idPrefix}_username`}
              aria-
              {...register('username', { required: true })}
              control={control}
              autocomplete="off"
              isRequired={isRequired(schema, 'username', true)}
              value={data?.username}
              errorText={intl.error(formState.errors.username)}
              labelText={intl.msg('username.label', 'Your User ID')}
              placeholderText={intl.msg('username.placeholder', ' ')}
            />
          </Field>
          <Field>
            <TextInput
              id={`${idPrefix}_password`}
              {...register('password', { required: true })}
              control={control}
              autocomplete="off"
              isRequired={isRequired(schema, 'password', true)}
              type="password"
              value={data?.password}
              errorText={intl.error(formState.errors.password)}
              labelText={intl.msg('password.label', 'Your Password')}
              placeholderText={intl.msg('password.placeholder', ' ')}
            />
          </Field>
          {children}
          <p className="under-input-text">
            {intl.msg('LoginForm.forgot', 'Forgot password?')}{' '}
            <S.Link to={'/auth/reset-password'}>{intl.msg('LoginForm.reset', 'Reset')}</S.Link>{' '}
            {intl.msg('LoginForm.or', 'or')}{' '}
            <S.Link to={'/auth/enter-code'}>
              {intl.msg('LoginForm.enter_code', 'Enter code')}
            </S.Link>
          </p>
          <S.FormFooter>
            <Button
              label={intl.msg('LoginForm.login', 'Login')}
              type="submit"
              className="login-button"
            ></Button>
          </S.FormFooter>
        </PolarisLoginBaseForm>
      </S.FormContainer>
    );
  }
);

type Data = {
  username: string;
  password: string;
};

type Props = {
  intlPrefix?: string;
  children?: any;
  data?: Data;
  mode?: Mode;
  schema?: ObjectSchema;
  idPrefix?: string;
  onChange?: (data: any) => void;
  onSubmit?: (data: any) => void;
  onError?: (error: any) => void;
  errors?: AxiosError<any, any>;
  fields?: string[];
  submitResponse?: any;
};
